import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API_BASE, formatErr, resolveMediaUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Copy, ImagePlus, MessageCircle, PackagePlus, Search, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const emptyPackage = { title: "", price: "", duration: "", description: "", features: "", is_active: true };
const emptyGallery = { image_url: "", title: "", caption: "", is_active: true };

function newId() {
  return globalThis.crypto?.randomUUID?.() || String(Date.now());
}

function listFromText(value) {
  return String(value || "").split(/[;,|]/).map((item) => item.trim()).filter(Boolean);
}

function textFromList(value) {
  return Array.isArray(value) ? value.join(", ") : "";
}

export default function AdAgencyStorefrontPanel() {
  const [me, setMe] = useState(null);
  const [profile, setProfile] = useState({
    headline: "",
    tagline: "",
    description: "",
    hours: "",
    is_open: true,
    image_url: "",
    announcement: "",
    whatsapp_number: "",
    coverage_areas: [],
    highlights: [],
    packages: [],
    gallery: [],
    banner_slides: [],
  });
  const [packageForm, setPackageForm] = useState(emptyPackage);
  const [galleryForm, setGalleryForm] = useState(emptyGallery);
  const [leads, setLeads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [seoForm, setSeoForm] = useState({ seo_title: "", meta_description: "", meta_keywords: "", canonical_url: "", og_image_url: "", business_address: "", google_map_url: "" });
  const [seoBusy, setSeoBusy] = useState(false);

  const publicUrl = useMemo(() => {
    if (me?.custom_booking_domain_status === "active" && me?.custom_booking_domain) {
      return `https://${me.custom_booking_domain}`;
    }
    const ref = me?.public_booking_slug || me?.id || "";
    return ref ? `${window.location.origin}/agency/${encodeURIComponent(ref)}` : "";
  }, [me]);

  const load = useCallback(async () => {
    try {
      const [meRes, leadsRes] = await Promise.all([
        api.get("/client/me"),
        api.get("/client/ad-agency/leads").catch(() => ({ data: [] })),
      ]);
      const seoRes = await api.get("/client/seo-settings").catch(() => ({ data: {} }));
      setMe(meRes.data);
      setSeoForm((prev) => ({ ...prev, ...(seoRes.data || {}) }));
      const stored = meRes.data.ad_agency_profile || {};
      setProfile({
        headline: stored.headline || "",
        tagline: stored.tagline || "",
        description: stored.description || "",
        hours: stored.hours || "",
        is_open: stored.is_open !== false,
        image_url: stored.image_url || "",
        announcement: stored.announcement || "",
        whatsapp_number: stored.whatsapp_number || meRes.data.phone || "",
        coverage_areas: Array.isArray(stored.coverage_areas) ? stored.coverage_areas : [],
        highlights: Array.isArray(stored.highlights) ? stored.highlights : [],
        packages: Array.isArray(stored.packages) ? stored.packages : [],
        gallery: Array.isArray(stored.gallery) ? stored.gallery : [],
        banner_slides: Array.isArray(stored.banner_slides) ? stored.banner_slides : [],
      });
      setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadImage = async (file, onUrl) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "storefront");
      fd.append("zone", "main");
      fd.append("name", file.name);
      const { data } = await api.post("/client/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUrl(data.id ? `${API_BASE}/media/serve/${data.id}` : resolveMediaUrl(data.public_url || data.url || ""));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const saveProfile = async () => {
    try {
      const { data } = await api.put("/client/ad-agency/profile", profile);
      setProfile(data);
      toast.success("Ad Agency storefront saved");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const saveSeo = async () => {
    setSeoBusy(true);
    try {
      const { data } = await api.put("/client/seo-settings", seoForm);
      setSeoForm((prev) => ({ ...prev, ...(data || {}) }));
      toast.success("Ad Agency SEO settings saved");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSeoBusy(false);
    }
  };

  const addPackage = () => {
    if (!packageForm.title.trim()) return;
    setProfile((prev) => ({
      ...prev,
      packages: [
        ...(prev.packages || []),
        {
          id: newId(),
          title: packageForm.title.trim(),
          price: packageForm.price.trim(),
          duration: packageForm.duration.trim(),
          description: packageForm.description.trim(),
          features: listFromText(packageForm.features),
          is_active: packageForm.is_active,
        },
      ],
    }));
    setPackageForm(emptyPackage);
  };

  const addGallery = () => {
    if (!galleryForm.image_url.trim()) return;
    setProfile((prev) => ({
      ...prev,
      gallery: [...(prev.gallery || []), { id: newId(), ...galleryForm }],
    }));
    setGalleryForm(emptyGallery);
  };

  const removeItem = (key, id) => {
    setProfile((prev) => ({ ...prev, [key]: (prev[key] || []).filter((item) => item.id !== id) }));
  };

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.put(`/client/ad-agency/leads/${leadId}/status`, { status });
      toast.success("Lead updated");
      load();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const copyLink = async () => {
    if (!publicUrl) return;
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Agency page link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <div>
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="rounded-sm bg-[#F3F4F6] flex-wrap h-auto p-2 gap-2">
          <TabsTrigger value="profile" className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white">Agency page</TabsTrigger>
          <TabsTrigger value="packages" className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white">Packages</TabsTrigger>
          <TabsTrigger value="gallery" className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white">Ad gallery</TabsTrigger>
          <TabsTrigger value="seo" className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white">SEO settings</TabsTrigger>
          <TabsTrigger value="leads" className="rounded-sm data-[state=active]:bg-[#111827] data-[state=active]:text-white">Enquiries</TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <div className="bg-[#111827] text-white rounded-sm p-5 md:p-6">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/60">Public Ad Agency page</div>
                <h3 className="mt-2 font-display text-3xl font-extrabold tracking-tight">Advertising storefront</h3>
                <p className="mt-2 text-sm text-white/70">Manage digital hoarding booking, LED wall advertising, indoor screen ads and lead capture content.</p>
              </div>
              <div className="flex items-center gap-3 bg-white/10 px-4 py-3 border border-white/10">
                <Label className="text-xs text-white/70">Accept enquiries</Label>
                <Switch checked={profile.is_open !== false} onCheckedChange={(v) => setProfile({ ...profile, is_open: v })} />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2 mt-6">
              <div><Label className="text-white/70">Headline</Label><Input value={profile.headline} onChange={(e) => setProfile({ ...profile, headline: e.target.value })} className="mt-1 rounded-sm bg-white text-[#111827]" placeholder="Book premium ad slots across Vasai" /></div>
              <div><Label className="text-white/70">Tagline</Label><Input value={profile.tagline} onChange={(e) => setProfile({ ...profile, tagline: e.target.value })} className="mt-1 rounded-sm bg-white text-[#111827]" placeholder="Digital hoardings, LED walls and indoor screens" /></div>
              <div className="md:col-span-2"><Label className="text-white/70">Description</Label><Textarea value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} className="mt-1 rounded-sm bg-white text-[#111827]" rows={3} /></div>
              <div><Label className="text-white/70">Hours</Label><Input value={profile.hours} onChange={(e) => setProfile({ ...profile, hours: e.target.value })} className="mt-1 rounded-sm bg-white text-[#111827]" placeholder="Mon-Sat, 10 AM-7 PM" /></div>
              <div><Label className="text-white/70">WhatsApp number</Label><Input value={profile.whatsapp_number} onChange={(e) => setProfile({ ...profile, whatsapp_number: e.target.value })} className="mt-1 rounded-sm bg-white text-[#111827]" /></div>
              <div><Label className="text-white/70">Announcement</Label><Input value={profile.announcement} onChange={(e) => setProfile({ ...profile, announcement: e.target.value })} className="mt-1 rounded-sm bg-white text-[#111827]" placeholder="Festival campaign slots open" /></div>
              <div><Label className="text-white/70">Coverage areas</Label><Input value={textFromList(profile.coverage_areas)} onChange={(e) => setProfile({ ...profile, coverage_areas: listFromText(e.target.value) })} className="mt-1 rounded-sm bg-white text-[#111827]" placeholder="Vasai, Virar, Mumbai" /></div>
              <div className="md:col-span-2"><Label className="text-white/70">Highlights</Label><Input value={textFromList(profile.highlights)} onChange={(e) => setProfile({ ...profile, highlights: listFromText(e.target.value) })} className="mt-1 rounded-sm bg-white text-[#111827]" placeholder="LED walls, Mall screens, Campaign reporting" /></div>
              <div className="md:col-span-2">
                <Label className="text-white/70">Cover image</Label>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <div className="w-32 h-20 bg-white/10 border border-white/10 overflow-hidden">
                    {profile.image_url ? <img src={resolveMediaUrl(profile.image_url)} alt="" className="w-full h-full object-cover" /> : null}
                  </div>
                  <label className={`inline-flex items-center gap-2 px-3 py-2 border border-white/10 bg-white/10 text-sm cursor-pointer ${uploading ? "opacity-60" : ""}`}>
                    <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { uploadImage(e.target.files?.[0], (url) => setProfile((p) => ({ ...p, image_url: url }))); e.target.value = ""; }} />
                    <Upload className="w-4 h-4" /> Upload cover
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-5 border-t border-white/10 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">Public route</div>
                <div className="font-mono text-xs text-white/80 break-all">{publicUrl || "Loading..."}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={copyLink} className="rounded-sm bg-white/10 border-white/15 text-white hover:bg-white/20"><Copy className="w-4 h-4 mr-2" /> Copy</Button>
                <Button onClick={saveProfile} className="rounded-sm bg-white text-[#111827] hover:bg-white/90">Save agency page</Button>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="packages">
          <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
              <h3 className="font-display text-2xl font-extrabold tracking-tight">Add campaign package</h3>
              <div className="grid gap-4 mt-5">
                <div><Label>Title</Label><Input value={packageForm.title} onChange={(e) => setPackageForm({ ...packageForm, title: e.target.value })} className="mt-1 rounded-sm" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Price / offer</Label><Input value={packageForm.price} onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })} className="mt-1 rounded-sm" /></div>
                  <div><Label>Duration</Label><Input value={packageForm.duration} onChange={(e) => setPackageForm({ ...packageForm, duration: e.target.value })} className="mt-1 rounded-sm" /></div>
                </div>
                <div><Label>Description</Label><Textarea value={packageForm.description} onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })} className="mt-1 rounded-sm" rows={3} /></div>
                <div><Label>Features</Label><Input value={packageForm.features} onChange={(e) => setPackageForm({ ...packageForm, features: e.target.value })} className="mt-1 rounded-sm" placeholder="Digital hoarding, LED wall, Reporting" /></div>
                <Button onClick={addPackage} className="rounded-sm bg-[#111827] text-white"><PackagePlus className="w-4 h-4 mr-2" /> Add package</Button>
              </div>
            </div>
            <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
              <Table>
                <TableHeader><TableRow><TableHead>Package</TableHead><TableHead>Offer</TableHead><TableHead>Duration</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>
                  {(profile.packages || []).length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-10 text-sm text-[#6B7280]">No campaign packages yet.</TableCell></TableRow>}
                  {(profile.packages || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell><div className="font-semibold">{item.title}</div><div className="text-xs text-[#6B7280] line-clamp-1">{item.description}</div></TableCell>
                      <TableCell>{item.price || "Request quote"}</TableCell>
                      <TableCell>{item.duration || "-"}</TableCell>
                      <TableCell className="text-right"><Button size="icon" variant="ghost" className="text-red-600" onClick={() => removeItem("packages", item.id)}><Trash2 className="w-4 h-4" /></Button></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="p-4 border-t border-[#E5E7EB] text-right"><Button onClick={saveProfile} className="rounded-sm bg-[#111827] text-white">Save packages</Button></div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="gallery">
          <div className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
            <div className="bg-white border border-[#E5E7EB] rounded-sm p-5">
              <h3 className="font-display text-2xl font-extrabold tracking-tight">Client ad gallery</h3>
              <div className="grid gap-4 mt-5">
                <div><Label>Image URL</Label><Input value={galleryForm.image_url} onChange={(e) => setGalleryForm({ ...galleryForm, image_url: e.target.value })} className="mt-1 rounded-sm" /></div>
                <label className={`inline-flex items-center justify-center gap-2 px-3 py-2 border border-[#E5E7EB] bg-[#F9FAFB] text-sm cursor-pointer ${uploading ? "opacity-60" : ""}`}>
                  <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => { uploadImage(e.target.files?.[0], (url) => setGalleryForm((p) => ({ ...p, image_url: url }))); e.target.value = ""; }} />
                  <ImagePlus className="w-4 h-4" /> Upload image
                </label>
                <div><Label>Title</Label><Input value={galleryForm.title} onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })} className="mt-1 rounded-sm" /></div>
                <div><Label>Caption</Label><Input value={galleryForm.caption} onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })} className="mt-1 rounded-sm" /></div>
                <Button onClick={addGallery} className="rounded-sm bg-[#111827] text-white">Add gallery item</Button>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {(profile.gallery || []).length === 0 && <div className="sm:col-span-2 border border-dashed border-[#D1D5DB] bg-white p-10 text-center text-sm text-[#6B7280]">No gallery items yet.</div>}
              {(profile.gallery || []).map((item) => (
                <div key={item.id} className="bg-white border border-[#E5E7EB] overflow-hidden">
                  <div className="aspect-[16/10] bg-[#F3F4F6]">{item.image_url && <img src={resolveMediaUrl(item.image_url)} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="p-4">
                    <div className="font-semibold">{item.title || "Gallery item"}</div>
                    <div className="text-xs text-[#6B7280] mt-1">{item.caption}</div>
                    <Button size="sm" variant="ghost" className="mt-3 text-red-600" onClick={() => removeItem("gallery", item.id)}><Trash2 className="w-4 h-4 mr-2" /> Remove</Button>
                  </div>
                </div>
              ))}
              {(profile.gallery || []).length > 0 && <div className="sm:col-span-2 text-right"><Button onClick={saveProfile} className="rounded-sm bg-[#111827] text-white">Save gallery</Button></div>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="seo">
          <div className="bg-[#111827] text-white rounded-sm p-5 md:p-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/60">Search engine settings</div>
                <h3 className="mt-2 font-display text-3xl font-extrabold tracking-tight">Ad Agency SEO</h3>
                <p className="mt-2 max-w-2xl text-sm text-white/70">
                  Tune how this agency page appears on Google, WhatsApp, Facebook and shared campaign links.
                </p>
              </div>
              <Search className="h-8 w-8 text-white/50" />
            </div>

            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <Label className="text-white/70">SEO title</Label>
                <Input
                  value={seoForm.seo_title || ""}
                  onChange={(e) => setSeoForm({ ...seoForm, seo_title: e.target.value })}
                  className="mt-1 rounded-sm bg-white text-[#111827]"
                  placeholder="Digital Hoarding Booking & LED Wall Advertising in Vasai"
                />
              </div>
              <div>
                <Label className="text-white/70">Meta keywords</Label>
                <Input
                  value={seoForm.meta_keywords || ""}
                  onChange={(e) => setSeoForm({ ...seoForm, meta_keywords: e.target.value })}
                  className="mt-1 rounded-sm bg-white text-[#111827]"
                  placeholder="digital hoarding booking, LED wall advertising, indoor screen ads"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-white/70">Meta description</Label>
                <Textarea
                  value={seoForm.meta_description || ""}
                  onChange={(e) => setSeoForm({ ...seoForm, meta_description: e.target.value })}
                  className="mt-1 rounded-sm bg-white text-[#111827]"
                  rows={3}
                  placeholder="Book digital hoardings, LED wall ads, indoor screen slots and campaign packages for local brand promotions."
                />
              </div>
              <div>
                <Label className="text-white/70">Canonical URL</Label>
                <Input
                  value={seoForm.canonical_url || ""}
                  onChange={(e) => setSeoForm({ ...seoForm, canonical_url: e.target.value })}
                  className="mt-1 rounded-sm bg-white text-[#111827]"
                  placeholder={publicUrl || "https://yourdomain.com/agency/your-slug"}
                />
              </div>
              <div>
                <Label className="text-white/70">OG / share image URL</Label>
                <Input
                  value={seoForm.og_image_url || ""}
                  onChange={(e) => setSeoForm({ ...seoForm, og_image_url: e.target.value })}
                  className="mt-1 rounded-sm bg-white text-[#111827]"
                  placeholder="Image used when page is shared"
                />
              </div>
              <div>
                <Label className="text-white/70">Business address</Label>
                <Input
                  value={seoForm.business_address || ""}
                  onChange={(e) => setSeoForm({ ...seoForm, business_address: e.target.value })}
                  className="mt-1 rounded-sm bg-white text-[#111827]"
                  placeholder="Shop / office address"
                />
              </div>
              <div>
                <Label className="text-white/70">Google Map URL</Label>
                <Input
                  value={seoForm.google_map_url || ""}
                  onChange={(e) => setSeoForm({ ...seoForm, google_map_url: e.target.value })}
                  className="mt-1 rounded-sm bg-white text-[#111827]"
                  placeholder="https://maps.google.com/..."
                />
              </div>
            </div>

            <div className="mt-6 flex flex-col gap-3 border-t border-white/10 pt-5 md:flex-row md:items-center md:justify-between">
              <div className="text-xs text-white/60">
                These values are used by the public Ad Agency page at <span className="font-mono text-white/85">{publicUrl || "/agency/..."}</span>.
              </div>
              <Button onClick={saveSeo} disabled={seoBusy} className="rounded-sm bg-white text-[#111827] hover:bg-white/90">
                {seoBusy ? "Saving SEO..." : "Save SEO settings"}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="leads">
          <div className="bg-white border border-[#E5E7EB] rounded-sm overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Lead</TableHead><TableHead>Campaign</TableHead><TableHead>Budget</TableHead><TableHead>Status</TableHead><TableHead>Contact</TableHead></TableRow></TableHeader>
              <TableBody>
                {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-10 text-sm text-[#6B7280]">No ad enquiries yet.</TableCell></TableRow>}
                {leads.map((lead) => (
                  <TableRow key={lead.id}>
                    <TableCell><div className="font-semibold">{lead.name}</div><div className="text-xs text-[#6B7280]">{lead.company || lead.email}</div></TableCell>
                    <TableCell><div className="text-sm font-medium">{lead.service || "Campaign enquiry"}</div><div className="text-xs text-[#6B7280] line-clamp-1">{lead.message}</div></TableCell>
                    <TableCell>{lead.budget || "-"}</TableCell>
                    <TableCell>
                      <Select value={lead.status || "new"} onValueChange={(status) => updateLeadStatus(lead.id, status)}>
                        <SelectTrigger className="rounded-sm w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{["new", "contacted", "quoted", "won", "lost"].map((status) => <SelectItem key={status} value={status}>{status}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-2 text-sm font-semibold text-[#111827] hover:underline"><MessageCircle className="w-4 h-4" /> {lead.phone}</a>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
